import { useCallback, useMemo } from 'react';
import { NextRouter, useRouter } from 'next/router';
import { ParsedUrlQuery, ParsedUrlQueryInput } from 'querystring';

import { convertToArray, extractFirstValue } from 'shared/utils';

function pushQuery(router: NextRouter, query: ParsedUrlQueryInput) {
  return router.push(
    {
      pathname: router.pathname,
      query,
    },
    undefined,
    {
      shallow: true,
    },
  );
}

function replaceQuery(router: NextRouter, query: ParsedUrlQueryInput) {
  return router.replace(
    {
      pathname: router.pathname,
      query,
    },
    undefined,
    {
      shallow: true,
    },
  );
}

interface UseQueryReturn {
  parseQueryValueString: (key: string) => string;
  parseQueryValueArray: (key: string) => string[];
  pushQuery: (newQuery: ParsedUrlQueryInput) => Promise<boolean>;
  replaceQuery: (newQuery: ParsedUrlQueryInput) => Promise<boolean>;
  query: ParsedUrlQuery;
}

export default function useQuery(): UseQueryReturn {
  const router = useRouter();

  const parseQueryValueString = useCallback(
    (key: string): string => {
      return extractFirstValue(router.query[key]);
    },
    [router],
  );

  const parseQueryValueArray = useCallback(
    (key: string): string[] => {
      return convertToArray(router.query[key]);
    },
    [router],
  );

  return useMemo(
    () => ({
      parseQueryValueString,
      parseQueryValueArray,
      pushQuery: (newQuery: ParsedUrlQueryInput) => pushQuery(router, newQuery),
      replaceQuery: (newQuery: ParsedUrlQueryInput) =>
        replaceQuery(router, newQuery),
      query: router.query,
    }),
    [parseQueryValueString, parseQueryValueArray, router],
  );
}
